import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Layout } from "../components/tag/Layout";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { api } from "../lib/api";

const PRODUCT_LABELS = {
  audit: "Authority Audit ($49)",
  blueprint: "Authority Blueprint ($497) — Summary",
  launch_kit: "AuthorityOS Launch Kit ($2,500)",
  build: "AuthorityOS Build ($7,500)",
  build_content_engine: "AuthorityOS Build + Content Engine ($10,000)",
  authority_gap_playbook: "The Authority Gap Playbook ($19)",
  authorityos_starter_guide: "AuthorityOS Starter Guide ($19)",
  content_engine_blueprint: "Content Engine Blueprint ($19)",
  ai_authority_toolkit: "AI Authority Toolkit ($19)",
};

const TIER_LABELS = {
  lead: "Lead",
  audit: "Audit Customer",
  blueprint: "Blueprint Customer",
  launch_kit: "Launch Kit Customer",
  build: "Build Customer",
  build_content_engine: "Build + Content Engine Customer",
};

export default function PortalDashboard() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => { refresh(); /* refresh once on mount */ }, [refresh]);

  if (!user) return null;
  const tier = user.tier || "lead";
  const tierLabel = TIER_LABELS[tier] || tier;

  return (
    <Layout title="Customer Portal | The Authority Gap" path="/portal">
      <section style={{ background: "#090909", padding: "40px 20px 80px", minHeight: "60vh" }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
            <div>
              <p style={{ color: "#FF6575", fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 600, marginBottom: 6 }}>{tierLabel}</p>
              <h1 className="tag-h1" data-testid="portal-greeting" style={{ marginBottom: 4, fontSize: 32 }}>Welcome, {user.name || user.email}.</h1>
              <p style={{ color: "#888", fontSize: 13 }}>{user.email}</p>
            </div>
            <button data-testid="portal-logout" onClick={() => { logout(); nav("/portal/login"); }} className="btn-ghost" style={{ padding: "8px 16px", fontSize: 12 }}>
              Sign out
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 6, borderBottom: "1px solid #222", marginBottom: 28 }}>
            {["overview", "brain", "deliverables"].map((t) => (
              <button
                key={t}
                data-testid={`portal-tab-${t}`}
                onClick={() => setTab(t)}
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: tab === t ? "2px solid #FF6575" : "2px solid transparent",
                  color: tab === t ? "#fff" : "#888",
                  padding: "10px 16px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  textTransform: "capitalize",
                }}
              >
                {t === "brain" ? "Authority Brain" : t}
              </button>
            ))}
          </div>

          {tab === "overview" && <OverviewTab user={user} />}
          {tab === "brain" && <BrainTab user={user} />}
          {tab === "deliverables" && <DeliverablesTab user={user} />}
        </div>
      </section>
    </Layout>
  );
}

function OverviewTab({ user }) {
  const aiq = user.ai_brain || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
      <Card title="Tier">
        <div style={{ fontSize: 24, color: "#fff", fontFamily: "var(--font-display)" }}>{TIER_LABELS[user.tier] || user.tier}</div>
        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{user.purchases?.length || 0} purchases on file</div>
      </Card>
      <Card title="Authority Brain access">
        <div style={{ fontSize: 18, color: "#fff", fontFamily: "var(--font-display)" }}>
          {aiq.unlimited ? "Unlimited" : `${aiq.remaining_today ?? 0}/${aiq.quota_per_day ?? 0}`}
        </div>
        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
          {aiq.unlimited ? "No daily cap on your tier" : "Messages remaining today"}
        </div>
      </Card>
      <Card title="Account">
        <div style={{ fontSize: 13, color: "#bbb", marginTop: 2 }}>{user.email}</div>
        <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>Joined {new Date(user.created_at).toLocaleDateString()}</div>
      </Card>
    </div>
  );
}

function DeliverablesTab({ user }) {
  const purchases = user.purchases || [];
  if (purchases.length === 0) {
    return <div style={{ color: "#888", fontSize: 13 }}>No deliverables yet.</div>;
  }
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {purchases.map((p) => (
        <div key={p.id || p.product} data-testid={`deliverable-${p.product}`} style={{
          background: "#0d0d0d", border: "1px solid #1a1a1a", borderRadius: 8, padding: 18,
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
        }}>
          <div>
            <div style={{ color: "#fff", fontFamily: "var(--font-display)", fontSize: 17 }}>
              {PRODUCT_LABELS[p.product] || p.product}
            </div>
            <div style={{ color: "#888", fontSize: 11, marginTop: 2 }}>
              {p.paid_at ? `Paid ${new Date(p.paid_at).toLocaleDateString()}` : "Paid"}
            </div>
            {p.product === "blueprint" && (
              <div data-testid={`bp-full-status-${p.id || ""}`} style={{
                marginTop: 8, fontSize: 11, color: p.full_blueprint_delivered_at ? "#7bd88f" : "#FF6575",
                fontWeight: 600, letterSpacing: 0.3,
              }}>
                {p.full_blueprint_delivered_at
                  ? `✓ Full Authority Blueprint delivered ${new Date(p.full_blueprint_delivered_at).toLocaleDateString()}`
                  : "Full Authority Blueprint pending — arrives within 3-5 business days"}
              </div>
            )}
          </div>
          <div>
            {p.ai_deliverable_url ? (
              <a href={p.ai_deliverable_url} target="_blank" rel="noreferrer" className="btn-primary" style={{ fontSize: 12, padding: "8px 14px" }}>
                Download PDF
              </a>
            ) : p.download_url ? (
              <a href={p.download_url} target="_blank" rel="noreferrer" className="btn-primary" style={{ fontSize: 12, padding: "8px 14px" }}>
                Download
              </a>
            ) : (
              <span style={{ color: "#777", fontSize: 11 }}>Generating…</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function BrainTab({ user }) {
  const { refresh: refreshAuth } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [quotaInfo, setQuotaInfo] = useState({
    quota_per_day: user.ai_brain?.quota_per_day,
    used_today: user.ai_brain?.used_today,
    remaining_today: user.ai_brain?.remaining_today,
    unlimited: user.ai_brain?.unlimited,
  });
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || busy) return;
    const userMsg = input.trim();
    setMessages((m) => [...m, { role: "user", text: userMsg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/portal/brain/chat", {
        message: userMsg,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "brain", text: data.reply }]);
      setQuotaInfo({
        quota_per_day: data.quota_per_day,
        used_today: data.used_today,
        remaining_today: data.remaining_today,
        unlimited: data.quota_per_day == null,
      });
      // Refresh global user state so Overview tab sees the new quota count.
      refreshAuth();
    } catch (err) {
      const msg = formatApiError(err);
      toast.error(msg);
      setMessages((m) => [...m, { role: "brain", text: `⚠️ ${msg}` }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="brain-tab">
      <div style={{ background: "#0d0d0d", border: "1px solid #1a1a1a", borderRadius: 8, padding: 14, marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 12, color: "#888" }}>
          <span style={{ color: "#FF6575", fontWeight: 600 }}>Authority Brain</span> · Tier: <strong style={{ color: "#fff" }}>{TIER_LABELS[user.tier] || user.tier}</strong>
        </div>
        <div data-testid="brain-quota" style={{ fontSize: 12, color: "#aaa" }}>
          {quotaInfo.unlimited ? "Unlimited messages today" : `${quotaInfo.remaining_today ?? "—"}/${quotaInfo.quota_per_day ?? "—"} remaining`}
        </div>
      </div>

      <div data-testid="brain-messages" style={{
        background: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: 8,
        padding: 18, minHeight: 360, maxHeight: 500, overflowY: "auto", marginBottom: 12,
      }}>
        {messages.length === 0 && (
          <p style={{ color: "#666", fontSize: 13, textAlign: "center", padding: "40px 0" }}>
            Ask the Authority Brain anything strategic — funnel design, content cadence, positioning, offer pricing.
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ marginBottom: 14, textAlign: m.role === "user" ? "right" : "left" }}>
            <div style={{
              display: "inline-block", maxWidth: "85%", padding: "10px 14px", borderRadius: 8,
              background: m.role === "user" ? "#FF6575" : "#181818",
              color: m.role === "user" ? "#fff" : "#e0e0e0",
              fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap", textAlign: "left",
            }}>{m.text}</div>
          </div>
        ))}
        {busy && (
          <div style={{ color: "#888", fontSize: 12, fontStyle: "italic" }}>Brain is thinking…</div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} style={{ display: "flex", gap: 8 }}>
        <input
          data-testid="brain-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="What's your question?"
          className="tag-input"
          style={{ flex: 1, marginBottom: 0 }}
          disabled={busy}
        />
        <button data-testid="brain-send" type="submit" className="btn-primary" disabled={busy || !input.trim()}>Send</button>
      </form>

      <p style={{ color: "#666", fontSize: 11, marginTop: 10, lineHeight: 1.6 }}>
        Educational only. Not legal, financial, tax, or professional advice. Conversation history is stored to your account.
      </p>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: "#0d0d0d", border: "1px solid #1a1a1a", borderRadius: 8, padding: 18 }}>
      <div style={{ color: "#FF6575", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}
